import { useEffect } from 'react';

interface SEOConfig {
  title: string;
  description: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
}

export function useSEO(config: SEOConfig) {
  useEffect(() => {
    // Update title immediately
    document.title = config.title;

    // Update meta description
    const updateMeta = (selector: string, attribute: string, value: string) => {
      let element = document.querySelector(selector) as HTMLMetaElement;
      if (element) {
        element.setAttribute(attribute, value);
      } else {
        element = document.createElement('meta');
        if (selector.includes('name=')) {
          element.name = selector.match(/name="([^"]+)"/)?.[1] || '';
        } else if (selector.includes('property=')) {
          element.setAttribute('property', selector.match(/property="([^"]+)"/)?.[1] || '');
        }
        element.content = value;
        document.head.appendChild(element);
      }
    };

    // Update canonical link
    const updateCanonical = (href: string) => {
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (canonicalLink) {
        canonicalLink.href = href;
      } else {
        canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        canonicalLink.href = href;
        document.head.appendChild(canonicalLink);
      }
    };

    const defaultImage = 'https://text.onl/og-image.png';

    // Apply all updates
    updateMeta('meta[name="description"]', 'content', config.description);
    updateCanonical(config.canonical);
    
    // Open Graph
    updateMeta('meta[property="og:title"]', 'content', config.ogTitle || config.title);
    updateMeta('meta[property="og:description"]', 'content', config.ogDescription || config.description);
    updateMeta('meta[property="og:url"]', 'content', config.canonical);
    updateMeta('meta[property="og:image"]', 'content', config.ogImage || defaultImage);
    updateMeta('meta[property="og:type"]', 'content', config.ogType || 'website');
    updateMeta('meta[property="og:site_name"]', 'content', 'Text.onl');
    
    // Twitter Card
    updateMeta('meta[name="twitter:card"]', 'content', config.twitterCard || 'summary_large_image');
    updateMeta('meta[name="twitter:title"]', 'content', config.twitterTitle || config.ogTitle || config.title);
    updateMeta('meta[name="twitter:description"]', 'content', config.twitterDescription || config.ogDescription || config.description);
    updateMeta('meta[name="twitter:image"]', 'content', config.twitterImage || config.ogImage || defaultImage);
    updateMeta('meta[name="twitter:site"]', 'content', '@textonl');
    
  }, [config.title, config.description, config.canonical, config.ogTitle, config.ogDescription, config.ogImage, config.ogType, config.twitterTitle, config.twitterDescription, config.twitterImage, config.twitterCard]);
}